import { observer } from 'mobx-react-lite'
import { Platform, StyleSheet, TouchableOpacity, StatusBar as StatusBarRN } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { DS } from '../../design-system'
import { useThrottledCallback } from 'use-debounce'
import { StatusBarStyle, StatusBar } from 'expo-status-bar'
import { ScheduledGameCard } from '../../components/ScheduledGameCard/ScheduledGameCard'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { useCallback, useMemo, useState } from 'react'
import { useRouter } from 'expo-router'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { translate } from '../../i18n'
import { formatBlindIncrease, readScheduleGameConfig } from '../../utils'

interface AcceptScheduledTournamentScreenProps {
  hash: string
}

export const AcceptScheduledTournamentScreen = observer(({ hash }: AcceptScheduledTournamentScreenProps) => {
  const router = useRouter()
  const {
    session: { user },
    analytics
  } = useStore()

  const userId = user?.auth0Id ?? ''

  const scheduleResult = useQuery(
    api.gameSchedules.queries.getScheduleByHash,
    hash ? { hash, userId: userId || undefined } : 'skip'
  )

  const isRestricted = scheduleResult != null && 'restricted' in scheduleResult && scheduleResult.restricted
  const schedule = isRestricted
    ? null
    : ((scheduleResult as Exclude<typeof scheduleResult, { restricted: true }>) ?? null)

  const gameId = schedule ? schedule.createdGameIds?.[0] : undefined
  const game = useQuery(api.games.queries.getGame, gameId ? { gameId } : 'skip')

  const acceptInvite = useMutation(api.gameSchedules.mutations.acceptPrivateScheduleInvite)
  const joinScheduledGame = useMutation(api.gameSchedules.mutations.joinPrivateScheduledGame)

  const [isAdding, setIsAdding] = useState(false)
  const [isJoining, setIsJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)

  const inviteList = useMemo(() => schedule?.usersToNotify ?? [], [schedule])
  const isMyUserInInviteList = useMemo(() => inviteList.includes(userId), [inviteList, userId])
  const isHost = schedule?.hostId === userId

  const showAddGame = useMemo(() => {
    if (isHost) return false
    if (isAdding) return true
    return !isMyUserInInviteList
  }, [isMyUserInInviteList, isAdding, isHost])

  // Allow join when game is pregame or playing, or no game exists yet (first joiner creates lobby).
  // The host is included: joining creates the lobby on demand, which is how they start the game.
  const gameStateJoinable = game?.state === 'pregame' || game?.state === 'playing'
  const noGameYet = schedule && !gameId
  const showJoinGame = Boolean(schedule && (gameStateJoinable || noGameYet))

  const handleJoinGame = useCallback(async () => {
    if (!schedule || !userId) return
    setIsJoining(true)
    setJoinError(null)
    try {
      const userName = user?.name ?? 'Player'
      // acceptPrivateScheduleInvite rejects the host ("Host is already in the schedule"),
      // so only non-hosts accept the invite before joining.
      if (!isHost) {
        await acceptInvite({
          scheduleId: schedule._id,
          userId,
          userName
        })
        analytics.setEvent(events.game.schedule_game_added, {
          game_name: schedule.name
        })
      }
      const result = await joinScheduledGame({
        scheduleId: schedule._id,
        userId,
        userName
      })
      router.replace({ pathname: '/game/[gameId]', params: { gameId: result.gameId } } as Parameters<
        typeof router.replace
      >[0])
    } catch (error) {
      // Any failure (closed invites, lounge full, server error, etc.) surfaces the same
      // friendly message — never a raw Convex error.
      console.error('Join game error:', error)
      setJoinError(translate('acceptScheduledTournamentScreen.joinError'))
      setIsJoining(false)
    }
  }, [schedule, userId, user?.name, isHost, acceptInvite, analytics, joinScheduledGame, router])

  const scheduleConfig = readScheduleGameConfig(schedule)
  const formattedSchedule = schedule
    ? {
        name: schedule.name,
        gameType: 'Tournament',
        blindTime: formatBlindIncrease(scheduleConfig.blindTimer),
        startingChips: String(scheduleConfig.startingChips ?? 0),
        turnDuration: `${scheduleConfig.turnDuration ?? 30} sec`,
        scheduledAt: new Date(schedule.scheduledStartTime),
        hash: schedule.hash ?? '',
        hostedBy: schedule.hostName,
        isHost: schedule.hostId === userId
      }
    : null

  const onBack = useThrottledCallback(
    () => {
      if (router.canDismiss()) {
        return router.dismiss()
      }
      if (router.canGoBack()) {
        return router.back()
      }
      router.dismissTo('/(root)/(tabs)/play')
    },
    350,
    { leading: true }
  )

  const handleAddGameInvitation = useCallback(async () => {
    if (!schedule) return
    if (!userId) return
    setIsAdding(true)
    setJoinError(null)
    try {
      const userName = user?.name ?? 'Player'
      await acceptInvite({
        scheduleId: schedule._id,
        userId,
        userName
      })
      router.dismissTo('/(root)/(tabs)/play')
      analytics.setEvent(events.game.schedule_game_added, {
        game_name: schedule.name
      })
    } catch (error) {
      console.error(error)
      setJoinError(translate('acceptScheduledTournamentScreen.joinError'))
      setIsAdding(false)
    }
  }, [schedule, userId, user?.name, acceptInvite, router, analytics])

  if (!hash) {
    router.dismissTo('/(root)/(tabs)/play')
    return null
  }

  // Not found (null returned from query, not undefined which means loading)
  if (scheduleResult === null && !isRestricted) {
    router.replace(`/(root)/game/join/${hash}`)
    return null
  }

  return (
    <SafeAreaView style={styles.screen}>
      <StatusBar animated={true} style={'light' as StatusBarStyle} hidden={false} />
      <DS.Frame style={styles.container}>
        <DS.Frame gap="24" style={{ paddingTop: 24 }}>
          <DS.Frame row justifyContent="space-between" alignItems="center">
            <TouchableOpacity onPress={onBack}>
              <DS.Icon name="Close" size={24} color="Global/White" />
            </TouchableOpacity>
          </DS.Frame>
          <DS.Text variant="Heading 1/Medium" color="Brand/Blue">
            {translate('acceptScheduledTournamentScreen.title')}
          </DS.Text>

          {isRestricted ? (
            <DS.Text variant="Body Medium/Regular" color="Global/White">
              {translate('acceptScheduledTournamentScreen.restrictedMessage')}
            </DS.Text>
          ) : formattedSchedule ? (
            <ScheduledGameCard {...formattedSchedule} />
          ) : (
            <DS.Text variant="Body Medium/Regular" color="Global/White">
              {translate('acceptScheduledTournamentScreen.loading')}
            </DS.Text>
          )}
        </DS.Frame>

        {!isRestricted && showAddGame && formattedSchedule && (
          <DS.Button
            onPress={handleAddGameInvitation}
            variant="Large/Primary"
            type="On Dark"
            disabled={isAdding}
            style={{ marginTop: DS.spacing['16'] }}
          >
            {isAdding
              ? translate('acceptScheduledTournamentScreen.adding')
              : translate('acceptScheduledTournamentScreen.addGame')}
          </DS.Button>
        )}

        {!isRestricted && showJoinGame && (
          <DS.Button
            onPress={handleJoinGame}
            variant="Large/Primary"
            type="On Dark"
            disabled={isJoining}
            style={{ marginTop: DS.spacing['16'] }}
          >
            {isJoining
              ? translate('acceptScheduledTournamentScreen.joining')
              : isHost && noGameYet
                ? translate('common.startNow')
                : translate('acceptScheduledTournamentScreen.joinGame')}
          </DS.Button>
        )}

        {joinError && (
          <DS.Text
            variant="Body Medium/Regular"
            color="Utility/Error"
            style={{ marginTop: DS.spacing['12'], textAlign: 'center' }}
          >
            {joinError}
          </DS.Text>
        )}
      </DS.Frame>
    </SafeAreaView>
  )
})

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Purple'],
    paddingTop: Platform.OS === 'android' ? StatusBarRN.currentHeight : 0
  },
  container: {
    paddingBottom: DS.spacing['16'],
    paddingHorizontal: 24,
    flex: 1
  }
})
